import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import mobileBackground from "../assets/brand/backgrounds/DG2Mobile.webp";
import logoPencaBlueHorizontal from "../assets/brand/penca/LogoPencoAzul.webp";

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const PAGE_WIDTH_PX = 1240;
const PAGE_HEIGHT_PX = 1754;

const COLORS = {
  page: "#EEF4E5",
  card: "#FFFFFF",
  cardAlt: "#FAFAF8",
  blue: "#143287",
  blueText: "#1B3F99",
  text: "#4A5A7A",
  border: "#D6E0F2",
};

let mobileBackgroundDataUrlPromise = null;

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }

      reject(new Error("No pudimos preparar la imagen."));
    };

    reader.onerror = () => reject(new Error("No pudimos preparar la imagen."));
    reader.readAsDataURL(blob);
  });
}

function waitForFonts() {
  if (typeof document === "undefined" || !document.fonts) {
    return Promise.resolve();
  }

  return Promise.allSettled([
    document.fonts.ready,
    document.fonts.load('600 32px "Quincy CF"'),
    document.fonts.load('500 18px "Inter"'),
    document.fonts.load('400 18px "Inter"'),
  ]).then(() => undefined);
}

async function getMobileBackgroundDataUrl() {
  if (!mobileBackgroundDataUrlPromise) {
    mobileBackgroundDataUrlPromise = fetch(mobileBackground)
      .then((response) => response.blob())
      .then((blob) => blobToDataUrl(blob));
  }

  return mobileBackgroundDataUrlPromise;
}

async function loadLogoDataUrl() {
  const response = await fetch(logoPencaBlueHorizontal);
  const blob = await response.blob();
  return blobToDataUrl(blob);
}

export async function buildTnuaQrDataUrl(qrToken) {
  return QRCode.toDataURL(qrToken, {
    errorCorrectionLevel: "M",
    margin: 1,
    width: 768,
    color: {
      dark: COLORS.blue,
      light: "#FFFFFF",
    },
  });
}

function createPdfPageElement({ tnua, qrDataUrl, backgroundDataUrl, logoDataUrl }) {
  const page = document.createElement("div");
  page.style.width = `${PAGE_WIDTH_PX}px`;
  page.style.height = `${PAGE_HEIGHT_PX}px`;
  page.style.position = "fixed";
  page.style.left = "-20000px";
  page.style.top = "0";
  page.style.overflow = "hidden";
  page.style.boxSizing = "border-box";
  page.style.backgroundImage = `url(${backgroundDataUrl})`;
  page.style.backgroundSize = "cover";
  page.style.backgroundPosition = "center";
  page.style.backgroundRepeat = "no-repeat";
  page.style.fontFamily = '"Inter", sans-serif';
  page.style.webkitFontSmoothing = "antialiased";
  page.style.textRendering = "geometricPrecision";

  const card = document.createElement("div");
  card.style.position = "absolute";
  card.style.left = "48px";
  card.style.top = "48px";
  card.style.width = "1144px";
  card.style.height = "1658px";
  card.style.background = COLORS.card;
  card.style.borderRadius = "44px";
  card.style.overflow = "hidden";
  card.style.boxSizing = "border-box";
  card.style.boxShadow = "0 6px 24px rgba(20, 50, 135, 0.08)";
  card.style.display = "flex";
  card.style.flexDirection = "column";

  const header = document.createElement("div");
  header.style.height = "160px";
  header.style.background = COLORS.blue;
  header.style.color = "#FFFFFF";
  header.style.display = "flex";
  header.style.alignItems = "center";
  header.style.justifyContent = "center";
  header.style.padding = "0 48px";
  header.style.boxSizing = "border-box";
  header.style.position = "relative";

  const title = document.createElement("div");
  title.textContent = tnua.name;
  title.style.fontFamily = '"Quincy CF", serif';
  title.style.fontSize = `${tnua.name.length > 22 ? 30 : tnua.name.length > 16 ? 34 : 40}px`;
  title.style.fontWeight = "600";
  title.style.lineHeight = "1";
  title.style.letterSpacing = "0.02em";
  title.style.textAlign = "center";
  title.style.whiteSpace = "nowrap";
  title.style.overflow = "visible";
  title.style.textOverflow = "unset";
  title.style.maxWidth = "100%";
  title.style.position = "absolute";
  title.style.left = "50%";
  title.style.top = "50%";
  title.style.transform = "translate(-50%, -70%)";
  header.appendChild(title);

  const main = document.createElement("div");
  main.style.padding = "56px 60px 56px";
  main.style.boxSizing = "border-box";
  main.style.display = "flex";
  main.style.flexDirection = "column";
  main.style.gap = "20px";
  main.style.flex = "1";
  main.style.minHeight = "0";

  const qrCard = document.createElement("div");
  qrCard.style.background = COLORS.cardAlt;
  qrCard.style.borderRadius = "40px";
  qrCard.style.padding = "28px 30px 14px";
  qrCard.style.boxSizing = "border-box";
  qrCard.style.marginBottom = "20px";
  qrCard.style.flex = "0 0 auto";

  const qrFrame = document.createElement("div");
  qrFrame.style.width = "840px";
  qrFrame.style.height = "840px";
  qrFrame.style.margin = "0 auto";
  qrFrame.style.border = "12px solid #3A6EEA";
  qrFrame.style.borderRadius = "46px";
  qrFrame.style.background = "#FFFFFF";
  qrFrame.style.display = "flex";
  qrFrame.style.alignItems = "center";
  qrFrame.style.justifyContent = "center";
  qrFrame.style.boxSizing = "border-box";

  const qrImage = document.createElement("img");
  qrImage.src = qrDataUrl;
  qrImage.alt = `QR de ${tnua.name}`;
  qrImage.style.width = "726px";
  qrImage.style.height = "726px";
  qrImage.style.display = "block";
  qrFrame.appendChild(qrImage);

  qrCard.appendChild(qrFrame);

  const bottom = document.createElement("div");
  bottom.style.display = "grid";
  bottom.style.gridTemplateColumns = "1.08fr 0.92fr";
  bottom.style.gap = "0";
  bottom.style.background = "#FFFFFF";
  bottom.style.borderRadius = "34px";
  bottom.style.overflow = "hidden";
  bottom.style.minHeight = "0";
  bottom.style.flex = "1";
  bottom.style.border = `1px solid ${COLORS.border}`;

  const logoColumn = document.createElement("div");
  logoColumn.style.display = "flex";
  logoColumn.style.alignItems = "center";
  logoColumn.style.justifyContent = "center";
  logoColumn.style.padding = "48px 24px";
  logoColumn.style.boxSizing = "border-box";

  const logoImage = document.createElement("img");
  logoImage.src = logoDataUrl;
  logoImage.alt = "Penca";
  logoImage.style.width = "360px";
  logoImage.style.maxWidth = "100%";
  logoImage.style.height = "auto";
  logoImage.style.objectFit = "contain";
  logoImage.style.display = "block";
  logoColumn.appendChild(logoImage);

  const instructionsColumn = document.createElement("div");
  instructionsColumn.style.borderLeft = `1px solid ${COLORS.border}`;
  instructionsColumn.style.padding = "48px 44px";
  instructionsColumn.style.boxSizing = "border-box";
  instructionsColumn.style.display = "flex";
  instructionsColumn.style.flexDirection = "column";
  instructionsColumn.style.justifyContent = "center";
  instructionsColumn.style.minHeight = "0";

  const instructionsTitle = document.createElement("div");
  instructionsTitle.textContent = "¿Cómo escaneo el QR?";
  instructionsTitle.style.fontFamily = '"Quincy CF", serif';
  instructionsTitle.style.fontSize = "34px";
  instructionsTitle.style.fontWeight = "600";
  instructionsTitle.style.lineHeight = "1.05";
  instructionsTitle.style.color = COLORS.blueText;
  instructionsTitle.style.marginBottom = "22px";

  const list = document.createElement("ol");
  list.style.margin = "0";
  list.style.padding = "0";
  list.style.listStyle = "none";
  list.style.counterReset = "step";
  list.style.display = "grid";
  list.style.gap = "16px";

  const steps = [
    "Abrí la app y tocá Escanear QR.",
    "Apuntá la cámara al código.",
    "Esperá la confirmación y listo.",
  ];

  for (const step of steps) {
    const item = document.createElement("li");
    item.style.display = "flex";
    item.style.gap = "14px";
    item.style.alignItems = "flex-start";
    item.style.fontFamily = '"Inter", sans-serif';
    item.style.fontSize = "22px";
    item.style.lineHeight = "1.35";
    item.style.color = COLORS.text;

    const number = document.createElement("span");
    number.textContent = `${list.children.length + 1}.`;
    number.style.flex = "0 0 auto";
    number.style.fontWeight = "600";
    number.style.color = COLORS.blueText;
    number.style.minWidth = "26px";

    const text = document.createElement("span");
    text.textContent = step;

    item.appendChild(number);
    item.appendChild(text);
    list.appendChild(item);
  }

  instructionsColumn.appendChild(instructionsTitle);
  instructionsColumn.appendChild(list);

  bottom.appendChild(logoColumn);
  bottom.appendChild(instructionsColumn);

  main.appendChild(qrCard);
  main.appendChild(bottom);

  card.appendChild(header);
  card.appendChild(main);
  page.appendChild(card);

  return page;
}

export async function generateTnuaQrPdf({ tnua, qrDataUrl }) {
  await waitForFonts();

  const backgroundDataUrl = await getMobileBackgroundDataUrl();
  const logoDataUrl = await loadLogoDataUrl();
  const pageElement = createPdfPageElement({
    tnua,
    qrDataUrl,
    backgroundDataUrl,
    logoDataUrl,
  });

  document.body.appendChild(pageElement);

  try {
    const canvas = await html2canvas(pageElement, {
      backgroundColor: null,
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
      scrollX: 0,
      scrollY: 0,
      width: PAGE_WIDTH_PX,
      height: PAGE_HEIGHT_PX,
    });

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const imageDataUrl = canvas.toDataURL("image/jpeg", 0.97);
    pdf.addImage(imageDataUrl, "JPEG", 0, 0, A4_WIDTH_MM, A4_HEIGHT_MM, undefined, "FAST");

    const fileName = `qr-${tnua.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "tnua"}.pdf`;

    pdf.save(fileName);
  } finally {
    pageElement.remove();
  }
}
